import { useState, useEffect } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import {
  ProductInfo,
  ProductImages,
  FeaturesList,
  TechnicalDetails,
  FAQSection,
} from "../components/camperDetails";
import EnhancedBookingForm from "../components/camperDetails/EnhancedBookingForm";
import { Header, WhySunCatchers } from "../components/shared";
import LoadingSpinner from "../components/shared/LoadingSpinner";
import { CheckoutFooter } from "../components/checkout";
import { fetchVehicleDetails } from "../services/vehicleService";
import { useTranslation } from "../hooks/useTranslation";
import { MEDIA_URL } from '../config/constants';
import { SEO, generateProductSchema, generateFAQSchema, generateBreadcrumbSchema } from "../seo";
import "../styles/camperDetails/CamperDetailsBase.css";
import "../styles/camperDetails/SunCatchersTheme.css";

const CamperDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation('camperDetails');
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get vehicle ID from navigation state first, then route param, then default
  // This allows clicking different Sun Catchers to show the correct vehicle
  const vehicleId = location.state?.vehicleId || (id === 'sun-catchers' ? 1 : parseInt(id) || 1);

  useEffect(() => {
    let isCancelled = false;
    
    const loadVehicleData = async () => {
      try {
        setLoading(true);
        const data = await fetchVehicleDetails(vehicleId);
        
        // Only update state if the component is still mounted and request wasn't cancelled
        if (!isCancelled) {
          setVehicleData(data);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadVehicleData();
    
    // Cleanup function to cancel the request if component unmounts
    return () => {
      isCancelled = true;
    };
  }, [vehicleId]);

  // For dynamic vehicle IDs, check vehicle_design from loaded data
  if (vehicleData && vehicleData.VehicleDesign?.String === 'Wave Catcher') {
    return <Navigate to="/campervan/wave-catchers" replace />;
  }

  if (loading) {
    return <LoadingSpinner text={t('loading')} />;
  }

  if (error || !vehicleData) {
    return <div>Error loading vehicle details: {error}</div>;
  }

  // Static images for Sun Catcher - simpler than database
  const staticImages = [
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-1.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-2.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-3.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-4.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-5.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-6.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-7.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-8.webp`,
    `${MEDIA_URL}/images/SUN_CATCHER/Sun-Catcher-Campervan-9.webp`,
  ];

  const mainImage = staticImages[0];
  const thumbnailImages = staticImages.slice(1, 4); // Show 3 thumbnails
  const allImages = staticImages; // All images for gallery modal

  // Transform API data to component format
  const camperDataFormatted = {
    id: vehicleData.ID.toString(),
    name: vehicleData.Name?.String || "Sun Catchers",
    description: vehicleData.Description?.String || "Meet the Sun Catcher, our bright, solar-powered camper designed for comfort and charm.",
    price: `$${vehicleData.DailyRate?.String || "86.48"}`,
    priceDescription: "Per Night",
    mainImage: mainImage,
    thumbnailImages: thumbnailImages,
    allImages: allImages, // All images for modal
    additionalImagesCount: 0,
    features: vehicleData.Features || {
      bathroom: [],
      bedroom: [],
      outdoor: [],
      kitchen: [],
      themeSpecs: [],
    },
    technicalDetails: [
      {
        icon: "van",
        title: t("technicalDetails.model"),
        description: "Fiat Ducato",
      },
      {
        icon: "engine",
        title: t("technicalDetails.power"),
        description: "115HP, 2.2l Engine",
      },
      {
        icon: "dimensions",
        title: t("technicalDetails.lengthHeight"),
        description: "541cm x 252cm",
      },
    ],
    faqs: t('sunCatcherFaqs', { returnObjects: true }).slice(0, 6).map((faq, index) => ({
      ...faq,
      isOpen: index === 0
    })),
  };

  const camper = camperDataFormatted;

  // SEO Schemas
  const productSchema = generateProductSchema({
    name: camper.name,
    description: camper.description,
    image: camper.mainImage,
    dailyRate: vehicleData.DailyRate?.String || '86.48',
    slug: 'campervan/sun-catchers',
    rating: 4.9,
    reviewCount: 23,
  });

  const faqSchema = generateFAQSchema(camper.faqs);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '' },
    { name: 'Catalog', path: '/catalog' },
    { name: camper.name, path: '/campervan/sun-catchers' },
  ]);

  return (
    <div className="camper-details-page">
      <SEO
        title={`${camper.name} | 4-Person Campervan Rental | SunCatchers`}
        description={camper.description}
        image={camper.mainImage}
        schema={[productSchema, faqSchema, breadcrumbSchema].filter(Boolean)}
      />
      <h1 className="sr-only">{camper.name} - Campervan Rental Portugal</h1>
      <div className="main-container">
        <Header />
        <div className="content-container">
          <div className="left-column">
            <div className="left-column-content">
              <ProductInfo
                name={camper.name}
                description={camper.description}
              />
              <ProductImages
                mainImage={camper.mainImage}
                thumbnailImages={camper.thumbnailImages}
                allImages={camper.allImages}
                additionalCount={camper.additionalImagesCount}
                vehicleName="Sun Catcher"
              />
            </div>
            <WhySunCatchers />
            <FeaturesList features={camper.features} />
            <TechnicalDetails details={camper.technicalDetails} />
            <FAQSection faqs={camper.faqs} />
          </div>
          <div className="booking-container">
            <EnhancedBookingForm
              price={camper.price}
              priceDescription={camper.priceDescription}
              vehicleId={vehicleId}
              vanTitle={camper.name}
              vanImage={camper.mainImage}
            />
          </div>
        </div>
      </div>
      <CheckoutFooter />
    </div>
  );
};

export default CamperDetails;
